import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectDarkMode } from "../../../app/store/slices/uiSlice";
import {
  PencilIcon,
  CheckIcon,
  ArrowLeftIcon,
  XMarkIcon,
  UserCircleIcon,
  EnvelopeIcon,
  LockClosedIcon,
  ShieldCheckIcon
} from "@heroicons/react/24/outline";

const getUserFromStorage = () => {
    const userProfile = JSON.parse(localStorage.getItem("user_profile") || "{}");
  let userName = "Admin User";
  if (userProfile?.firstName || userProfile?.lastName) {
    userName = `${userProfile?.firstName || ""} ${userProfile?.lastName || ""}`.trim();
  }
  const userRole =userProfile?.assignedRoles?.length ? userProfile.assignedRoles.map(item=>item.name).join(", ") : "Administrator"
 
  return {
    name: userName,
    email: localStorage.getItem("user_email") || "admin@vaxicare.com",
    role: userRole,
  };
};

export default function MyProfile() {
  const navigate = useNavigate();
  const darkMode = useSelector(selectDarkMode);
  const [user, setUser] = useState({ name: "", email: "", role: "" });
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({ name: "", email: "", password: "" });
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    const loggedUser = getUserFromStorage();
    setUser(loggedUser);
    setFormData({ ...loggedUser, password: "" });
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = (e) => {
    e.preventDefault();
    localStorage.setItem("user_name", formData.name);
    localStorage.setItem("user_email", formData.email);
    setUser({ ...user, name: formData.name, email: formData.email });
    setEditMode(false);
    setShowSuccess(true);
    setTimeout(() => setShowSuccess(false), 3000);
  };

  const handleCancel = () => {
    setFormData({ ...user, password: "" });
    setEditMode(false);
  };

  const userInitial = user.name?.charAt(0).toUpperCase() || "A";

  return (
    <div className={`min-h-screen p-4 lg:p-6 ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
      {/* Success Toast */}
      {showSuccess && (
        <div className="fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 flex items-center gap-2">
          <CheckIcon className="w-5 h-5" />
          Profile updated successfully!
        </div>
      )}

      {/* Header */}
      <div className="flex items-center mb-6">
        <button
          onClick={() => navigate(-1)}
          className={`flex items-center transition-colors mr-4 ${
            darkMode
              ? 'text-gray-400 hover:text-white'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <ArrowLeftIcon className="w-5 h-5 mr-2" />
          Back
        </button>
      </div>

      {/* Main Card */}
      <div className={`rounded-2xl shadow-lg border max-w-2xl mx-auto overflow-hidden ${
        darkMode
          ? 'bg-gray-800 border-gray-700'
          : 'bg-white border-gray-200'
      }`}>

        {/* Profile Header Section */}
        <div className={`px-6 py-8 border-b ${
          darkMode
            ? 'bg-gradient-to-r from-gray-800 to-gray-750 border-gray-700'
            : 'bg-gradient-to-r from-blue-50 to-indigo-50 border-gray-200'
        }`}>
          <div className="flex items-center">
            {/* Avatar */}
            <div className="relative">
              <div className="h-24 w-24 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white text-3xl font-bold shadow-lg">
                {userInitial}
              </div>
              <div className={`absolute -bottom-1 -right-1 w-8 h-8 rounded-full flex items-center justify-center ${
                darkMode ? 'bg-green-500' : 'bg-green-500'
              }`}>
                <ShieldCheckIcon className="w-5 h-5 text-white" />
              </div>
            </div>

            {/* User Info */}
            <div className="ml-6 flex-1">
              <h2 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                {user.name}
              </h2>
              <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                {user.email}
              </p>
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium mt-2 ${
                darkMode
                  ? 'bg-blue-900/50 text-blue-300'
                  : 'bg-blue-100 text-blue-700'
              }`}>
                {user.role}
              </span>
            </div>

            {/* Edit Button */}
            {!editMode && (
              <button
                onClick={() => setEditMode(true)}
                className="flex items-center px-4 py-2.5 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all shadow-md hover:shadow-lg"
              >
                <PencilIcon className="w-4 h-4 mr-2" />
                Edit Profile
              </button>
            )}
          </div>
        </div>

        {/* Form Section */}
        <div className="p-6">
          {editMode ? (
            <form onSubmit={handleSave} className="space-y-5">
              {/* Name Field */}
              <div>
                <label className={`flex items-center text-sm font-medium mb-2 ${
                  darkMode ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  <UserCircleIcon className="w-4 h-4 mr-2" />
                  Full Name
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className={`w-full rounded-lg px-4 py-3 border transition-all focus:ring-2 focus:ring-blue-500 focus:outline-none ${
                    darkMode
                      ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                  }`}
                  placeholder="Enter your full name"
                />
              </div>

              {/* Email Field */}
              <div>
                <label className={`flex items-center text-sm font-medium mb-2 ${
                  darkMode ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  <EnvelopeIcon className="w-4 h-4 mr-2" />
                  Email Address
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className={`w-full rounded-lg px-4 py-3 border transition-all focus:ring-2 focus:ring-blue-500 focus:outline-none ${
                    darkMode
                      ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                  }`}
                  placeholder="Enter your email"
                />
              </div>

              {/* Password Field */}
              <div>
                <label className={`flex items-center text-sm font-medium mb-2 ${
                  darkMode ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  <LockClosedIcon className="w-4 h-4 mr-2" />
                  New Password
                </label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Leave blank to keep current password"
                  className={`w-full rounded-lg px-4 py-3 border transition-all focus:ring-2 focus:ring-blue-500 focus:outline-none ${
                    darkMode
                      ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                  }`}
                />
                <p className={`text-xs mt-1.5 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  Only fill this if you want to change your password
                </p>
              </div>

              {/* Action Buttons */}
              <div className={`flex justify-end space-x-3 pt-4 border-t ${
                darkMode ? 'border-gray-700' : 'border-gray-200'
              }`}>
                <button
                  type="button"
                  onClick={handleCancel}
                  className={`flex items-center px-4 py-2.5 rounded-lg border transition-colors ${
                    darkMode
                      ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                      : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <XMarkIcon className="w-4 h-4 mr-2" />
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex items-center px-5 py-2.5 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all shadow-md"
                >
                  <CheckIcon className="w-4 h-4 mr-2" />
                  Save Changes
                </button>
              </div>
            </form>
          ) : (
            /* View Mode - Profile Details */
            <div className="space-y-4">
              <h3 className={`text-lg font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                Profile Information
              </h3>

              {/* Name */}
              <div className={`flex items-center p-4 rounded-lg ${
                darkMode ? 'bg-gray-700/50' : 'bg-gray-50'
              }`}>
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  darkMode ? 'bg-blue-900/50' : 'bg-blue-100'
                }`}>
                  <UserCircleIcon className={`w-5 h-5 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`} />
                </div>
                <div className="ml-4">
                  <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Full Name</p>
                  <p className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{user.name}</p>
                </div>
              </div>

              {/* Email */}
              <div className={`flex items-center p-4 rounded-lg ${
                darkMode ? 'bg-gray-700/50' : 'bg-gray-50'
              }`}>
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  darkMode ? 'bg-green-900/50' : 'bg-green-100'
                }`}>
                  <EnvelopeIcon className={`w-5 h-5 ${darkMode ? 'text-green-400' : 'text-green-600'}`} />
                </div>
                <div className="ml-4">
                  <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Email Address</p>
                  <p className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{user.email}</p>
                </div>
              </div>

              {/* Role */}
              <div className={`flex items-center p-4 rounded-lg ${
                darkMode ? 'bg-gray-700/50' : 'bg-gray-50'
              }`}>
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  darkMode ? 'bg-purple-900/50' : 'bg-purple-100'
                }`}>
                  <ShieldCheckIcon className={`w-5 h-5 ${darkMode ? 'text-purple-400' : 'text-purple-600'}`} />
                </div>
                <div className="ml-4">
                  <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Role</p>
                  <p className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{user.role}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
